import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addChatMessage, updateChatMessage } from './../../store/slices/roomSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faRedo } from '@fortawesome/free-solid-svg-icons';
import { FaLock } from 'react-icons/fa'; // For private message icon
import { toast } from 'react-toastify';
import api, {
  getChatMessages,
  getPrivateChatMessages,
  markRoomChatRead,
  markPrivateChatRead,
} from '../../utils/api';

import { clearChatAndWaitingBadges } from '../../notifications/badges';
import { getMeetingDisplayName } from '../../utils/meetingDisplayName';
import { buildMeetingChatAuditContext } from '../../utils/meetingChatAuditContext';

// --- SOUND ALERT FOR MESSAGES ---
const messageSound = new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3');

const getChatTimestamp = (message = {}) =>
  message.timestamp || message.createdAt || message.sentAt || '';

const getChatDate = (message = {}) => {
  const date = new Date(getChatTimestamp(message));
  return Number.isNaN(date.getTime()) ? null : date;
};

const chatDayFormatter = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const chatDateTimeFormatter = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

const formatChatDay = (message = {}) => {
  const date = getChatDate(message);
  if (!date) return 'Date unavailable';
  return chatDayFormatter.format(date);
};

const formatChatDateTime = (message = {}) => {
  const date = getChatDate(message);
  if (!date) return 'Date unavailable';
  return chatDateTimeFormatter.format(date);
};

const getChatParticipantUserId = (participant = {}) => String(
  participant.userId || participant.user?._id || participant.user || ''
);

const resolveActivePrivateTarget = (
  participants = [],
  userId = '',
  preferredSessionId = ''
) => {
  const normalizedUserId = String(userId || '');
  const normalizedPreferredSessionId = String(preferredSessionId || '');
  const candidates = participants.filter((participant) =>
    getChatParticipantUserId(participant) === normalizedUserId &&
    ['admitted', 'presenting'].includes(participant.status) &&
    participant.disconnected !== true &&
    Boolean(String(participant.sessionId || '').trim())
  );
  const participantTime = (participant) => new Date(
    participant.lastSeenAt || participant.rejoinedAt ||
      participant.joinedAt || participant.updatedAt || 0
  ).getTime();

  return [...candidates].sort((left, right) => {
    const timeDelta = participantTime(right) - participantTime(left);
    if (timeDelta) return timeDelta;
    const rightIsPreferred =
      String(right.sessionId) === normalizedPreferredSessionId;
    const leftIsPreferred =
      String(left.sessionId) === normalizedPreferredSessionId;
    if (rightIsPreferred !== leftIsPreferred) {
      return rightIsPreferred ? 1 : -1;
    }
    return candidates.indexOf(right) - candidates.indexOf(left);
  })[0] || null;
};

const Chat = ({ socket, isVisible = true, requestedTarget = null }) => {
  const [queue, setQueue] = useState([]);
  const [message, setMessage] = useState('');
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // ---- PRIVATE CHAT STATES ----
  const [privateMessages, setPrivateMessages] = useState([]); 
  const [activeChatTarget, setActiveChatTarget] = useState('everyone'); 
  const [activeTargetUserId, setActiveTargetUserId] = useState('');
  const [activeTargetName, setActiveTargetName] = useState('Everyone');
  const [privateReadRetryTick, setPrivateReadRetryTick] = useState(0);
  const privateReadRetryRef = useRef(new Map());

  const messagesEndRef = useRef(null);
  const offlineTargetNoticeRef = useRef('');
  const lastAppliedRequestedTargetRef = useRef('');
  const [isTyping, setIsTyping] = useState(false);
  const [connectionRetries, setConnectionRetries] = useState(0);
  const maxRetries = 3;

  const currentRoom = useSelector((s) => s.room.currentRoom);
  const user = useSelector((s) => s.auth.user);
  const chatMessages = useSelector((s) => s.room.currentRoom?.chatMessages || []);
  const participants = useSelector((s) => s.room.currentRoom?.participants || []);
  const dispatch = useDispatch();

  const myId = user?.id || user?._id || sessionStorage.getItem('userId');
  const mySessionId = sessionStorage.getItem('sessionId');

  // dedupe ref
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = chatMessages; }, [chatMessages]);

  const selectPrivateTarget = useCallback((target = {}) => {
    const userId = String(target.userId || '').trim();
    if (!userId) return;
    const requestedSessionId = String(
      target.sessionId || target.senderSessionId || ''
    ).trim();
    const currentTarget = resolveActivePrivateTarget(
      participants,
      userId,
      requestedSessionId
    );

    // A persisted private message can contain the sender's old session after
    // a rejoin. Always resolve the current admitted session for that user;
    // never prepare a reply to an offline/stale session id.
    if (!currentTarget) {
      setActiveChatTarget(`offline:${userId}`);
      setActiveTargetUserId(userId);
      setActiveTargetName(`${target.username || 'Participant'} (offline)`);
      const offlineNoticeKey = String(target.requestId || userId);
      if (offlineTargetNoticeRef.current !== offlineNoticeKey) {
        offlineTargetNoticeRef.current = offlineNoticeKey;
        toast.info(`${target.username || 'Participant'} is currently offline.`);
      }
      return;
    }
    offlineTargetNoticeRef.current = '';
    setActiveChatTarget(String(currentTarget.sessionId));
    setActiveTargetUserId(userId);
    setActiveTargetName(
      target.username || getMeetingDisplayName(currentTarget) || 'Participant'
    );
  }, [participants]);

  // ---- Listen for Open Private Chat Event from Participants Component ----
  useEffect(() => {
    const handleOpenPrivateChat = (e) => {
      selectPrivateTarget(e.detail || {});
    };
    window.addEventListener('open-private-chat', handleOpenPrivateChat);
    return () => window.removeEventListener('open-private-chat', handleOpenPrivateChat);
  }, [selectPrivateTarget]);

  useEffect(() => {
    if (!requestedTarget) return;
    const requestKey = String(
      requestedTarget.requestId ||
        `${requestedTarget.isPrivate ? 'private' : 'global'}:${requestedTarget.userId || ''}:${requestedTarget.sessionId || ''}`
    );
    if (lastAppliedRequestedTargetRef.current === requestKey) return;
    lastAppliedRequestedTargetRef.current = requestKey;
    if (requestedTarget.isPrivate) {
      selectPrivateTarget(requestedTarget);
      return;
    }
    setActiveChatTarget('everyone');
    setActiveTargetUserId('');
    setActiveTargetName('Everyone');
  }, [requestedTarget, selectPrivateTarget]);

  useEffect(() => {
    if (!activeTargetUserId || activeChatTarget === 'everyone') return;
    const currentTarget = resolveActivePrivateTarget(
      participants,
      activeTargetUserId,
      activeChatTarget
    );
    if (!currentTarget) {
      const offlineTarget = `offline:${activeTargetUserId}`;
      if (activeChatTarget !== offlineTarget) setActiveChatTarget(offlineTarget);
      setActiveTargetName((previous) =>
        previous.includes('(offline)') ? previous : `${previous} (offline)`
      );
      return;
    }

    const currentSessionId = String(currentTarget.sessionId);
    if (activeChatTarget !== currentSessionId) {
      setActiveChatTarget(currentSessionId);
    }
    setActiveTargetName(
      getMeetingDisplayName(currentTarget) ||
        activeTargetName.replace(/\s*\(offline\)\s*$/i, '') ||
        'Participant'
    );
  }, [
    activeChatTarget,
    activeTargetName,
    activeTargetUserId,
    participants,
  ]);

  // ---- read helpers ----
  const admittedRecipientsFor = useCallback((msg) => {
    return (participants || []).filter(
      p => p.status === 'admitted' && String(p.user?._id || p.user) !== String(msg.userId)
    );
  }, [participants]);

  const readCountFor = useCallback((msg) => {
    const readSet = new Set((msg.readBy || []).map(String));
    return admittedRecipientsFor(msg).reduce((acc, p) => {
      const uid = String(p.user?._id || p.user);
      return acc + (readSet.has(uid) ? 1 : 0);
    }, 0);
  }, [admittedRecipientsFor]);

  const readStateFor = useCallback((msg) => {
    if (msg.isSending) return 'sending';
    if (msg.isFailed)  return 'failed';
    const recipients = admittedRecipientsFor(msg);
    if (recipients.length === 0) return 'sent';
    const rc = readCountFor(msg);
    if (rc === 0) return 'sent';          // ✓
    if (rc < recipients.length) return 'read-some'; // ✓✓ gray
    return 'read-all';                    // ✓✓ blue
  }, [admittedRecipientsFor, readCountFor]);

  const Tick = ({ state }) => {
    if (state === 'sending') return <span> · Sending…</span>;
    if (state === 'failed')  return <span className="text-red-400"> · Failed</span>;
    if (state === 'sent')    return <span> <svg className="w-4 h-4 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 13l3 3 6-8" /><path d="M10 13l3 3 7-10" /></svg></span>;
    if (state === 'read-some') return <span> <svg className="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 13l3 3 6-8" /><path d="M10 13l3 3 7-10" /></svg></span>;
    if (state === 'read-all')  return <span className="text-blue-400"> <svg className="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 13l3 3 6-8" /><path d="M10 13l3 3 7-10" /></svg></span>;
    return null;
  };

  // ---- cutoff-bound, coalesced public read receipts ----
  const pendingReadCutoffRef = useRef('');
  const markReadInFlightRef = useRef(false);

  const latestPublicCutoff = useCallback(() => {
    return messagesRef.current.reduce((latest, item) => {
      const value = getChatTimestamp(item);
      const valueMs = new Date(value).getTime();
      const latestMs = new Date(latest || 0).getTime();
      return Number.isFinite(valueMs) && valueMs > latestMs ? value : latest;
    }, '');
  }, []);

  const markRead = useCallback((visibleCutoff = '') => {
    if (!currentRoom?.roomId) return;
    const cutoff = visibleCutoff || latestPublicCutoff();
    const cutoffMs = new Date(cutoff).getTime();
    if (!cutoff || !Number.isFinite(cutoffMs)) return;

    const pendingMs = new Date(pendingReadCutoffRef.current || 0).getTime();
    if (!pendingReadCutoffRef.current || cutoffMs > pendingMs) {
      pendingReadCutoffRef.current = cutoff;
    }
    if (markReadInFlightRef.current) return;

    markReadInFlightRef.current = true;
    void (async () => {
      let failedAttempts = 0;
      while (pendingReadCutoffRef.current) {
        const nextCutoff = pendingReadCutoffRef.current;
        pendingReadCutoffRef.current = '';
        try {
          await markRoomChatRead(currentRoom.roomId, nextCutoff);
          failedAttempts = 0;
          window.dispatchEvent(new CustomEvent('meeting-chat-unread-changed'));
        } catch {
          failedAttempts += 1;
          if (failedAttempts > 3) break;
          const queuedMs = new Date(pendingReadCutoffRef.current || 0).getTime();
          if (!pendingReadCutoffRef.current || new Date(nextCutoff).getTime() > queuedMs) {
            pendingReadCutoffRef.current = nextCutoff;
          }
          await new Promise((resolve) => {
            window.setTimeout(resolve, Math.min(5000, failedAttempts * 1200));
          });
        }
      }
    })().finally(() => {
      markReadInFlightRef.current = false;
      if (pendingReadCutoffRef.current) markRead();
    });
  }, [currentRoom?.roomId, latestPublicCutoff]);

  // Load history
  useEffect(() => {
    const loadMessages = async () => {
      try {
        const [publicResponse, privateResponse] = await Promise.all([
          getChatMessages(currentRoom.roomId),
          getPrivateChatMessages(currentRoom.roomId),
        ]);
        const publicMessages = publicResponse.data || [];
        publicMessages.forEach((m) => dispatch(addChatMessage(m)));
        setPrivateMessages(privateResponse.data || []);
        if (isVisible && publicMessages.length) {
          const latestLoadedCutoff = publicMessages.reduce((latest, item) => {
            const value = getChatTimestamp(item);
            return new Date(value).getTime() > new Date(latest || 0).getTime()
              ? value
              : latest;
          }, '');
          markRead(latestLoadedCutoff);
        }
      } catch {
        toast.error('Failed to load chat history');
      } finally {
        setIsLoading(false);
        await markChatRead();            
        await clearChatAndWaitingBadges(); 
      }
    };
    if (currentRoom?.roomId) loadMessages();
  }, [currentRoom?.roomId, dispatch, markRead, isVisible]);

  async function markChatRead() {
    try {
      await api.post('/me/counters/reset', { unreadChats: 0 }); 
    } catch (e) { console.warn('reset counters failed', e); }
  }

  // process queue
  const processQueue = useCallback(async () => {
    if (!isSocketConnected || queue.length === 0) return;
    const success = [];
    const failed = [];
    for (const msg of queue) {
      try {
        await sendMessage(msg.text, msg.isPrivate, msg.target);
        success.push(msg);
      } catch {
        failed.push(msg);
      }
    }
    setQueue(failed);
    if (success.length) toast.success(`Sent ${success.length} messages`);
    if (failed.length) toast.error(`Failed to send ${failed.length} messages`);
  }, [isSocketConnected, queue]); 

  // socket events
  const handleSocketEvents = useCallback(() => {
    if (!socket) return;

    const onConnect = () => {
      setIsSocketConnected(true);
      setConnectionRetries(0);
      if (queue.length > 0) processQueue();
    };
    const onDisconnect = () => {
      setIsSocketConnected(false);
      setConnectionRetries((r) => r + 1);
    };
    const onConnectError = () => {
      toast.error('Connection problem. Trying to reconnect...');
    };

    const onNewMessage = (msg) => {
      if (msg.clientTempId) {
        const optimistic = messagesRef.current.find(
          (m) => m.id === msg.clientTempId || m._id === msg.clientTempId
        );
        if (optimistic) {
          dispatch(updateChatMessage({
            id: optimistic.id,
            updates: { ...msg, isSending: false, isFailed: false, error: null, isOptimistic: false },
          }));
          if (isVisible) markRead(getChatTimestamp(msg));
          return;
        }
      }
      const exists = messagesRef.current.some((m) => m._id === msg._id || m.id === msg._id);
      if (!exists) {
        dispatch(addChatMessage(msg));
        if (isVisible) markRead(getChatTimestamp(msg));
        // Play sound for incoming global message
        if (String(msg.userId) !== String(myId)) {
          messageSound.play().catch(e => console.warn("Audio play blocked", e));
        }
      }
    };

    const onNewPrivateMessage = (msg) => {
      setPrivateMessages((prev) => {
        const exists = prev.some(
          (m) => String(m._id || m.id) === String(msg._id || msg.id)
        );
        if (exists) return prev;
        return [...prev, msg];
      });

      // Play sound for incoming private message
      if (msg.senderSessionId !== mySessionId) {
        messageSound.play().catch(e => console.warn("Audio play blocked", e));
        // If the panel is already open on Everyone, show the personal thread.
        if (activeChatTarget === 'everyone') {
          selectPrivateTarget({
            ...msg,
            sessionId: msg.senderSessionId,
            requestId: msg._id || msg.id || msg.timestamp,
          });
        }
      }
    };

    const onChatRead = ({ userId, cutoff }) => {
      const cutoffMs = new Date(cutoff).getTime();
      messagesRef.current.forEach((m) => {
        const ts = getChatDate(m)?.getTime() || 0;
        if (ts <= cutoffMs && String(m.userId) !== String(userId)) {
          const already = (m.readBy || []).map(String).includes(String(userId));
          if (!already) {
            dispatch(updateChatMessage({
              id: m.id || m._id,
              updates: { readBy: [ ...(m.readBy || []), userId ] }
            }));
          }
        }
      });
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('newMessage', onNewMessage);
    socket.on('newPrivateMessage', onNewPrivateMessage);
    socket.on('chat:read', onChatRead);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('newMessage', onNewMessage);
      socket.off('newPrivateMessage', onNewPrivateMessage);
      socket.off('chat:read', onChatRead);
    };
  }, [socket, queue.length, dispatch, isVisible, markRead, processQueue, myId, activeChatTarget, mySessionId, selectPrivateTarget]);

  useEffect(() => {
    const cleanup = handleSocketEvents();
    return cleanup;
  }, [handleSocketEvents]);

  // retry reconnect
  useEffect(() => {
    if (!isSocketConnected && connectionRetries > 0 && connectionRetries <= maxRetries) {
      const t = setTimeout(() => {
        if (socket && !socket.connected) socket.connect();
      }, 2000 * connectionRetries);
      return () => clearTimeout(t);
    }
  }, [isSocketConnected, connectionRetries, socket]);

  // autoscroll + mark read
  useEffect(() => {
    if (!isLoading && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      if (isVisible && activeChatTarget === 'everyone') markRead();
    }
  }, [chatMessages, privateMessages, isLoading, isVisible, markRead, activeChatTarget]);

  // mark read on focus
  useEffect(() => {
    const onFocus = () => { if (isVisible && activeChatTarget === 'everyone') markRead(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isVisible, markRead, activeChatTarget]);

  useEffect(() => {
    if (
      !isVisible ||
      activeChatTarget === 'everyone' ||
      !activeTargetUserId ||
      !currentRoom?.roomId
    ) return undefined;

    const threadMessages = privateMessages.filter((item) =>
      String(item.userId) === String(activeTargetUserId) ||
      String(item.recipientUserId) === String(activeTargetUserId)
    );
    const cutoff = threadMessages.reduce((latest, item) => {
      const value = getChatTimestamp(item);
      return new Date(value).getTime() > new Date(latest || 0).getTime()
        ? value
        : latest;
    }, '');
    if (!cutoff) return undefined;
    const cutoffMs = new Date(cutoff).getTime();

    let cancelled = false;
    let retryTimer = null;
    const retryKey = `${currentRoom.roomId}:${activeTargetUserId}:${cutoff}`;
    void markPrivateChatRead(currentRoom.roomId, activeTargetUserId, cutoff)
      .then(() => {
        if (cancelled) return;
        privateReadRetryRef.current.delete(retryKey);
        setPrivateMessages((prev) => prev.map((item) =>
          String(item.recipientUserId) === String(myId) &&
          String(item.userId) === String(activeTargetUserId) &&
          (getChatDate(item)?.getTime() || 0) <= cutoffMs
            ? { ...item, readAt: item.readAt || new Date().toISOString() }
            : item
        ));
        window.dispatchEvent(new CustomEvent('meeting-chat-unread-changed'));
      })
      .catch(() => {
        if (cancelled) return;
        const attempts = (privateReadRetryRef.current.get(retryKey) || 0) + 1;
        privateReadRetryRef.current.set(retryKey, attempts);
        if (attempts <= 3) {
          retryTimer = window.setTimeout(
            () => setPrivateReadRetryTick((value) => value + 1),
            Math.min(5000, attempts * 1200)
          );
        }
      });

    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [
    activeChatTarget,
    activeTargetUserId,
    currentRoom?.roomId,
    isVisible,
    myId,
    privateMessages.length,
    privateReadRetryTick,
  ]);

  // typing debounce
  useEffect(() => {
    if (!socket) return;
    const timer = window.setTimeout(() => {
      if (isTyping) {
        socket.emit('typing', false);
        setIsTyping(false);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [isTyping, socket]);

  // sendMessage
  const sendMessage = useCallback(
    async (msg, isPrivate = false, target = {}) => {
      if (!socket || !currentRoom?.roomId) throw new Error('Not ready to send messages');
      let targetSessionId = target.sessionId || activeChatTarget;
      const targetUserId = target.userId || activeTargetUserId;
      if (isPrivate && (!targetSessionId || !targetUserId || targetSessionId === 'everyone')) {
        throw new Error('Choose a participant for the personal message');
      }
      if (isPrivate) {
        const currentTarget = resolveActivePrivateTarget(
          participants,
          targetUserId,
          targetSessionId
        );
        if (!currentTarget) {
          throw new Error('Participant is offline. Personal message was not sent.');
        }
        targetSessionId = String(currentTarget.sessionId);
      }
      if (!socket.connected) {
        setQueue((prev) => [...prev, {
          text: msg,
          isPrivate,
          target: { sessionId: targetSessionId, userId: targetUserId },
        }]);
        throw new Error('Offline. Will retry…');
      }

      const tempId = `tmp-${Date.now()}`;
      const auditContext = buildMeetingChatAuditContext(currentRoom, mySessionId);

      if (isPrivate) {
        // --- SEND PRIVATE MESSAGE ---
        const pvtMsg = {
          id: tempId,
          userId: user.id,
          username: getMeetingDisplayName(user),
          message: msg.trim(),
          timestamp: new Date().toISOString(),
          isSending: true,
          isPrivate: true,
          targetSessionId,
          recipientUserId: targetUserId,
          senderSessionId: mySessionId,
          roomId: currentRoom.roomId,
          auditContext,
        };
        
        setPrivateMessages(prev => [...prev, pvtMsg]);

        return new Promise((resolve, reject) => {
          const timeout = window.setTimeout(() => {
            setPrivateMessages((prev) => prev.map((item) =>
              item.id === tempId
                ? { ...item, isSending: false, isFailed: true, error: 'No response from server' }
                : item
            ));
            reject(new Error('No response from server'));
          }, 8000);

          socket.emit('sendPrivateMessage', {
            message: msg.trim(),
            roomId: currentRoom.roomId,
            targetSessionId,
            targetUserId,
            clientTempId: tempId,
            auditContext,
          }, (resp) => {
            window.clearTimeout(timeout);
            if (resp?.success && resp.message) {
              setPrivateMessages((prev) => prev.map((item) =>
                item.id === tempId
                  ? { ...item, ...resp.message, isSending: false, isFailed: false, error: null }
                  : item
              ));
              resolve(resp.message);
            } else {
              const error = resp?.error || 'Personal message failed';
              setPrivateMessages((prev) => prev.map((item) =>
                item.id === tempId
                  ? { ...item, isSending: false, isFailed: true, error }
                  : item
              ));
              reject(new Error(error));
            }
          });
        });

      } else {
        // --- SEND GLOBAL MESSAGE ---
        const optimistic = {
          id: tempId, _id: tempId, userId: user.id, username: getMeetingDisplayName(user),
          message: msg.trim(), timestamp: new Date().toISOString(),
          isSending: true, isOptimistic: true, readBy: [],
          roomId: currentRoom.roomId,
          senderSessionId: mySessionId,
          auditContext,
        };
        dispatch(addChatMessage(optimistic));

        return new Promise((resolve, reject) => {
          const to = setTimeout(() => {
            dispatch(updateChatMessage({ id: tempId, updates: { isFailed: true, isSending: false, error: 'No response from server' } }));
            reject(new Error('No response from server'));
          }, 5000);

          socket.emit(
            'sendMessage',
            {
              message: msg.trim(),
              roomId: currentRoom.roomId,
              clientTempId: tempId,
              auditContext,
            },
            (resp) => {
              clearTimeout(to);
              if (!resp || resp.error) {
                dispatch(updateChatMessage({ id: tempId, updates: { isFailed: true, isSending: false, error: resp?.error || 'Unknown error' } }));
                reject(new Error(resp?.error || 'Unknown error'));
                return;
              }
              dispatch(updateChatMessage({
                id: tempId,
                updates: { ...resp.message, isSending: false, isFailed: false, error: null, isOptimistic: false },
              }));
              resolve();
            }
          );
        });
      }
    },
    [
      socket,
      currentRoom?.roomId,
      currentRoom?._id,
      currentRoom?.id,
      currentRoom?.meetingId,
      currentRoom?.meetingType,
      currentRoom?.meetingKind,
      currentRoom?.timetableMetadata,
      user,
      dispatch,
      activeChatTarget,
      activeTargetUserId,
      mySessionId,
      participants,
    ]
  );

  const handleRetry = async (msgObj) => {
    try {
      await sendMessage(msgObj.message, msgObj.isPrivate, {
        sessionId: msgObj.targetSessionId || activeChatTarget,
        userId: msgObj.recipientUserId || activeTargetUserId,
      });
      if (!msgObj.isPrivate) {
        dispatch(updateChatMessage({ id: msgObj.id, updates: { isFailed: false, isSending: true, error: null } }));
      }
    } catch (e) {
      toast.error(`Retry failed: ${e.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Message cannot be empty');
      return;
    }
    try {
      await sendMessage(message, activeChatTarget !== 'everyone');
      setMessage('');
    } catch (err) {
      toast.error(`Failed to send: ${err.message}`);
    }
  };

  if (!currentRoom || isLoading) {
    return (
      <div className="flex flex-col h-full bg-gray-800 text-white items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Generate dropdown options for private chat
  const seenTargetUsers = new Set();
  const chatOptions = [
    { id: 'everyone', userId: '', name: 'Everyone (Global)' },
    ...participants
      .filter((participant) => {
        const participantUserId = String(
          participant.userId || participant.user?._id || participant.user || ''
        );
        if (
          !['admitted', 'presenting'].includes(participant.status) ||
          participant.disconnected === true ||
          !participant.sessionId ||
          participant.sessionId === mySessionId ||
          !participantUserId ||
          participantUserId === String(myId) ||
          seenTargetUsers.has(participantUserId)
        ) return false;
        const currentTarget = resolveActivePrivateTarget(
          participants,
          participantUserId,
          participant.sessionId
        );
        if (String(currentTarget?.sessionId || '') !== String(participant.sessionId)) {
          return false;
        }
        seenTargetUsers.add(participantUserId);
        return true;
      })
      .map((participant) => ({
        id: participant.sessionId,
        userId: String(
          participant.userId || participant.user?._id || participant.user
        ),
        name: getMeetingDisplayName(participant),
        unread: privateMessages.filter((item) =>
          !item.readAt &&
          String(item.recipientUserId) === String(myId) &&
          String(item.userId) === String(
            participant.userId || participant.user?._id || participant.user
          )
        ).length,
      })),
  ];
  const activeTargetIsOffline =
    activeChatTarget.startsWith('offline:') && Boolean(activeTargetUserId);
  if (activeTargetIsOffline) {
    chatOptions.push({
      id: activeChatTarget,
      userId: activeTargetUserId,
      name: activeTargetName,
      unread: privateMessages.filter((item) =>
        !item.readAt &&
        String(item.recipientUserId) === String(myId) &&
        String(item.userId) === String(activeTargetUserId)
      ).length,
      offline: true,
    });
  }

  // Filter visible messages based on selection
  const visibleMessages = activeChatTarget === 'everyone' 
    ? chatMessages 
    : privateMessages.filter((item) =>
        activeTargetUserId
          ? String(item.userId) === String(activeTargetUserId) ||
            String(item.recipientUserId) === String(activeTargetUserId)
          : item.targetSessionId === activeChatTarget ||
            item.senderSessionId === activeChatTarget
      );

  return (
    <div className="meeting-chat flex min-h-0 min-w-0 flex-col h-full bg-gray-800 text-white overflow-hidden">
      
      {/* TARGET SELECTOR HEADER */}
      <div className="p-2 border-b border-gray-700 bg-gray-900 flex items-center justify-between">
        <span className="text-xs text-gray-400 mr-2">To:</span>
        <select 
          className="min-w-0 flex-1 bg-gray-700 text-white text-sm rounded px-2 py-1 outline-none border border-transparent focus:border-blue-500"
          value={activeChatTarget}
          onChange={(e) => {
            setActiveChatTarget(e.target.value);
            const opt = chatOptions.find(o => o.id === e.target.value);
            if (opt) {
              setActiveTargetUserId(opt.userId || '');
              setActiveTargetName(opt.name);
            }
          }}
        >
          {chatOptions.map(opt => (
            <option key={opt.id} value={opt.id}>
              {opt.name}{opt.unread ? ` (${opt.unread} unread)` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="meeting-chat-messages min-h-0 min-w-0 flex-1 overflow-y-auto p-3 space-y-3">
        {visibleMessages.length === 0 && (
          <div className="text-center text-gray-500 text-sm mt-10">No messages yet.</div>
        )}

        {visibleMessages.map((msg, i) => {
          const state = activeChatTarget === 'everyone' ? readStateFor(msg) : 'sent';
          const mine = String(msg.userId) === String(myId);
          const isPvt = msg.isPrivate;
          const dayLabel = formatChatDay(msg);
          const previousDay =
            i > 0 ? formatChatDay(visibleMessages[i - 1]) : '';
          const showDayDivider = i === 0 || dayLabel !== previousDay;

          return (
            <React.Fragment key={(msg._id || msg.id || 'message') + '_' + i}>
              {showDayDivider && (
                <div className="flex items-center gap-2 py-1" role="separator">
                  <span className="h-px flex-1 bg-gray-700" />
                  <span className="rounded-full bg-gray-900 px-2.5 py-1 text-[10px] font-medium text-gray-300">
                    {dayLabel}
                  </span>
                  <span className="h-px flex-1 bg-gray-700" />
                </div>
              )}
              <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`meeting-chat-bubble min-w-0 max-w-xs lg:max-w-md px-3 py-2 rounded-lg relative ${
                    mine ? 'bg-blue-200 text-black' : 'bg-gray-700 text-white'
                  } ${isPvt ? 'border border-amber-500 shadow-sm' : ''} ${msg.isSending ? 'opacity-70' : ''}`}
                >
                {isPvt && (
                  <div className={`absolute -top-2 ${mine ? 'right-2' : 'left-2'} bg-amber-500 text-black text-[9px] font-bold px-1.5 rounded flex items-center gap-1`}>
                    <FaLock size={8}/> Direct
                  </div>
                )}
                
                {msg.isFailed && (
                  <div className="text-xs text-red-500 mb-1 flex justify-between items-center">
                    <span>Failed: {msg.error}</span>
                    <button onClick={() => handleRetry(msg)} className="text-xs hover:text-black" title="Retry">
                      <FontAwesomeIcon icon={faRedo} />
                    </button>
                  </div>
                )}
                
                <div className={`text-xs font-semibold ${mine ? 'text-gray-600' : 'text-gray-300'}`}>
                  {mine ? 'You' : getMeetingDisplayName(msg)}
                </div>
                
                <div className="text-sm mt-1">{msg.message}</div>
                
                <div className={`text-xs mt-1 ${mine ? 'text-gray-600' : 'text-gray-200/70'} text-right flex`} style={{justifyContent:'space-between'}}>
                  <time dateTime={getChatTimestamp(msg) || undefined}>
                    {formatChatDateTime(msg)}
                  </time>
                  <div style={{paddingLeft:'5px'}}> {mine && !isPvt && <Tick state={state} />} </div>
                </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="p-3 border-t border-gray-700 bg-gray-800">
        <div className="flex min-w-0 space-x-2">
          <input
            type="text"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (!isTyping && socket) {
                setIsTyping(true);
                socket.emit('typing', true);
              }
            }}
            placeholder={
              activeChatTarget === 'everyone'
                ? 'Type a message...'
                : activeTargetIsOffline
                  ? `${activeTargetName} · personal message history`
                  : `Direct message to ${activeTargetName}...`
            }
            className="min-w-0 flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            disabled={!user || activeTargetIsOffline}
          />
          <button
            type="submit"
            title="Send"
            disabled={!message.trim() || !user || activeTargetIsOffline}
            className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${activeChatTarget === 'everyone' ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}`}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default React.memo(Chat);
